import { dismissToast, toastWarning } from 'admination-react'

import { buildUrl, currentActiveOrganisationId, setLiveRestart } from '@/core/api/http'
import { toastOffline } from './offlineToast'
import { createSseParser, type SseEvent } from './sse'

type LiveState = 'connecting' | 'live' | 'interrupted' | 'offline'

export type LiveHint = { kind: 'card' | 'gone'; coachingId: string }

type LiveDropCode = 'ended' | 'network' | 'silent' | 'catchup' | 'stale' | 'hint' | `status-${number}`

type LiveDropQuery = { dropped?: LiveDropCode; cursor?: string }

const WATCHDOG_MS = 35_000
const OFFLINE_AFTER_MS = 60_000
const WATCHDOG_TICK_MS = 1_000
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000] as const

const INTERRUPTED_MESSAGE = "Live-Aktualisierung unterbrochen – Anzeige wird nicht aktualisiert"

const SILENT_ONCE: ReadonlySet<LiveDropCode> = new Set(['network', 'ended', 'silent'])

type LiveConnectionDeps = {
  snapshot: Promise<string | void>
  onHint: (hint: LiveHint) => Promise<void>
  catchUp: () => Promise<string | void>
  onUnauthorized: () => void
}

const ORGANISATION_HEADER = 'X-Admination-Organisation'

function openLiveStream(signal: AbortSignal, query: LiveDropQuery): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'text/event-stream' }
  const organisationId = currentActiveOrganisationId()
  if (organisationId) headers[ORGANISATION_HEADER] = organisationId
  return fetch(buildUrl('/live', query), { method: 'GET', credentials: 'include', headers, signal })
}

class LiveDrop extends Error {
  constructor(
    message: string,
    readonly code: LiveDropCode,
    readonly offline = false,
  ) {
    super(message)
  }
}

function hintOf(kind: LiveHint['kind'], data: string): LiveHint {
  const body: unknown = JSON.parse(data)
  const c = body && typeof body === 'object' ? (body as { c?: unknown }).c : undefined
  if (typeof c !== 'string' || c === '') {
    throw new Error(`/live: "${kind}" event without a coaching id: ${data}`)
  }
  return { kind, coachingId: c }
}

export function startLiveConnection(deps: LiveConnectionDeps): () => void {
  let state: LiveState = 'connecting'
  let closed = false
  let attempt = 0
  let controller: AbortController | null = null
  let lastByteAt = Date.now()
  let waitingToReconnect: ReturnType<typeof setTimeout> | null = null
  let failures = 0
  let notLiveSince = Date.now()
  let stopped = false
  let amberToast: string | null = null
  let redToast: string | null = null
  let query: LiveDropQuery = {}
  let cursor: string | undefined
  let initializing = true
  let buffering = true
  let pendingHints = new Map<string, LiveHint>()

  function setState(next: LiveState) {
    if (next === state) return
    if (state === 'live') notLiveSince = Date.now()
    state = next
  }

  function dismissToasts() {
    if (amberToast !== null) dismissToast(amberToast)
    if (redToast !== null) dismissToast(redToast)
    amberToast = null
    redToast = null
  }

  function goOffline() {
    if (amberToast !== null) dismissToast(amberToast)
    amberToast = null
    if (redToast === null) redToast = toastOffline()
    setState('offline')
    failures = Math.max(failures, BACKOFF_MS.length - 1)
  }

  function stopAttempt() {
    attempt += 1
    controller?.abort()
    controller = null
  }

  function connect() {
    stopAttempt()
    const id = attempt
    const ctrl = new AbortController()
    controller = ctrl
    buffering = true
    pendingHints = new Map()
    lastByteAt = Date.now()
    void read(id, ctrl.signal).catch((cause: unknown) => {
      if (id === attempt) dropped(cause, cause instanceof LiveDrop ? cause.code : 'network')
    })
  }

  async function read(id: number, signal: AbortSignal) {
    const requestCursor = cursor
    const response = await openLiveStream(signal, { ...query, ...(requestCursor ? { cursor: requestCursor } : {}) })
    if (id !== attempt) return
    if (!response.ok || !response.body) {
      const { status } = response
      throw new LiveDrop(`GET /live answered ${status}`, `status-${status}`, status === 503)
    }
    const parser = createSseParser((event) => {
      if (id === attempt) handle(id, event, requestCursor)
    })
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    for (;;) {
      const { done, value } = await reader.read()
      if (id !== attempt) return
      if (done) throw new LiveDrop("GET /live ended without a closing event", 'ended')
      lastByteAt = Date.now()
      parser.push(decoder.decode(value, { stream: true }))
    }
  }

  async function ready(id: number, data: string, requestCursor: string | undefined) {
    buffering = true
    let replay: unknown
    try {
      replay = (JSON.parse(data) as { replay?: unknown } | null)?.replay
    } catch (error) { void error }
    try {
      if (!requestCursor || replay !== 'current') {
        cursor = undefined
        const nextCursor = await deps.catchUp()
        if (id !== attempt || closed) return
        cursor = nextCursor || undefined
      }
    } catch (cause) {
      if (id === attempt) dropped(cause, 'catchup')
      return
    }
    try {
      while (id === attempt && !closed && pendingHints.size > 0) {
        const batch = [...pendingHints.values()]
        pendingHints.clear()
        await Promise.all(batch.map((hint) => deps.onHint(hint)))
      }
    } catch (cause) {
      if (id === attempt) dropped(cause, 'hint')
      return
    }
    if (id !== attempt || closed) return
    buffering = false
    failures = 0
    dismissToasts()
    setState('live')
  }

  function handle(id: number, event: SseEvent, requestCursor: string | undefined) {
    switch (event.event) {
      case 'ready':
        query = {}
        void ready(id, event.data, requestCursor)
        return
      case 'card':
      case 'gone': {
        try {
          const hint = hintOf(event.event, event.data)
          if (buffering) pendingHints.set(hint.coachingId, hint)
          else void deps.onHint(hint).catch((cause: unknown) => {
            if (id === attempt) dropped(cause, 'hint')
          })
        } catch (cause) {
          if (id === attempt) dropped(cause, 'hint')
        }
        return
      }
      case 'reconnect':
      case 'overflow':
        if (state === 'live') setState('connecting')
        connect()
        return
      case 'stale':
        throw new LiveDrop("GET /live closed with \"stale\"", 'stale', true)
      case 'unauthorized':
        stopped = true
        stopAttempt()
        goOffline()
        deps.onUnauthorized()
        return
      default:
        return
    }
  }

  function dropped(cause: unknown, code: LiveDropCode) {
    if (closed || stopped) return
    stopAttempt()
    query = { dropped: code }
    if (code === 'catchup' || code === 'hint') cursor = undefined
    if (failures === 0 && SILENT_ONCE.has(code)) setState('connecting')
    else {
      console.error("Live-Verbindung unterbrochen:", cause)
      if (cause instanceof LiveDrop && cause.offline) goOffline()
      if (state !== 'offline') {
        if (amberToast === null) amberToast = toastWarning(INTERRUPTED_MESSAGE)
        setState('interrupted')
      }
    }
    const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)]
    failures += 1
    waitingToReconnect = setTimeout(() => {
      waitingToReconnect = null
      connect()
    }, delay)
  }

  setLiveRestart(() => {
    if (closed || stopped || controller === null) return false
    stopAttempt()
    if (state === 'live') setState('connecting')
    waitingToReconnect = setTimeout(() => {
      waitingToReconnect = null
      connect()
    }, BACKOFF_MS[0])
    return true
  })

  const watchdog = setInterval(() => {
    if (closed || stopped) return
    if (state !== 'live' && state !== 'offline' && Date.now() - notLiveSince >= OFFLINE_AFTER_MS) {
      goOffline()
    }
    if (initializing || waitingToReconnect !== null) return
    if (Date.now() - lastByteAt >= WATCHDOG_MS) {
      dropped(new Error(`GET /live silent for ${WATCHDOG_MS / 1000} s`), 'silent')
    }
  }, WATCHDOG_TICK_MS)

  void deps.snapshot.catch(() => undefined).then((snapshotCursor) => {
    if (closed || stopped) return
    cursor = snapshotCursor || undefined
    initializing = false
    connect()
  })

  return () => {
    if (closed) return
    closed = true
    setLiveRestart(null)
    clearInterval(watchdog)
    if (waitingToReconnect !== null) clearTimeout(waitingToReconnect)
    stopAttempt()
    dismissToasts()
  }
}
